import { storage } from '@/lib/storage';
import { ProductBatch } from '@/types';
import { nanoid } from 'nanoid';

const BATCHES_KEY = 'product_batches';

export const batchService = {
  getAll: (): ProductBatch[] => {
    return storage.get<ProductBatch[]>(BATCHES_KEY) || [];
  },

  getById: (id: string): ProductBatch | null => {
    const batches = batchService.getAll();
    return batches.find(b => b.id === id) || null;
  },

  getBatchesByProduct: (productId: string): ProductBatch[] => {
    const batches = batchService.getAll();
    // Sort by production date (oldest first) for FIFO, fallback to received date
    return batches
      .filter(b => b.product_id === productId && b.quantity > 0)
      .sort((a, b) => {
        const dateA = a.production_date || a.received_date;
        const dateB = b.production_date || b.received_date;
        return new Date(dateA).getTime() - new Date(dateB).getTime();
      });
  },

  createBatch: (
    productId: string,
    quantity: number,
    unitCost: number,
    productionDate?: string,
    batchNumber?: string
  ): ProductBatch => {
    const batches = batchService.getAll();
    const newBatch: ProductBatch = {
      id: nanoid(),
      product_id: productId,
      quantity: quantity,
      unit_cost: unitCost,
      production_date: productionDate,
      received_date: new Date().toISOString(),
      batch_number: batchNumber,
      created_at: new Date().toISOString(),
    };
    batches.push(newBatch);
    storage.set(BATCHES_KEY, batches);
    return newBatch;
  },

  updateBatch: (id: string, data: Partial<ProductBatch>): ProductBatch | null => {
    const batches = batchService.getAll();
    const index = batches.findIndex(b => b.id === id);
    if (index === -1) return null;
    
    batches[index] = {
      ...batches[index],
      ...data,
      id: batches[index].id,
      product_id: batches[index].product_id,
      created_at: batches[index].created_at,
    };
    storage.set(BATCHES_KEY, batches);
    return batches[index];
  },

  deleteBatch: (id: string): boolean => {
    const batches = batchService.getAll();
    const filtered = batches.filter(b => b.id !== id);
    if (filtered.length === batches.length) return false;
    storage.set(BATCHES_KEY, filtered);
    return true;
  },

  // FIFO deduction logic - deduct from oldest batches first
  deductFromOldestBatch: (productId: string, quantity: number): { 
    success: boolean; 
    batchesUsed: Array<{ batchId: string; quantity: number; productionDate?: string }> 
  } => {
    const batches = batchService.getBatchesByProduct(productId);
    const batchesUsed: Array<{ batchId: string; quantity: number; productionDate?: string }> = [];
    let remaining = quantity;

    for (const batch of batches) {
      if (remaining <= 0) break;
      
      const deductAmount = Math.min(batch.quantity, remaining);
      batch.quantity -= deductAmount;
      remaining -= deductAmount;
      
      batchesUsed.push({
        batchId: batch.id,
        quantity: deductAmount,
        productionDate: batch.production_date
      });

      if (batch.quantity === 0) {
        batchService.deleteBatch(batch.id);
      } else {
        batchService.updateBatch(batch.id, { quantity: batch.quantity });
      }
    }

    return {
      success: remaining === 0,
      batchesUsed
    };
  },

  // Calculate age in months
  getBatchAge: (batch: ProductBatch): number => {
    const date = batch.production_date || batch.received_date;
    const ageMs = Date.now() - new Date(date).getTime();
    return Math.floor(ageMs / (1000 * 60 * 60 * 24 * 30)); // Age in months
  },

  // Get batches older than threshold (default 36 months = 3 years)
  getOldBatches: (productId: string, monthsThreshold: number = 36): ProductBatch[] => {
    const batches = batchService.getBatchesByProduct(productId);
    return batches.filter(batch => batchService.getBatchAge(batch) >= monthsThreshold);
  },

  // Get total quantity across all batches for a product
  getTotalQuantity: (productId: string): number => {
    const batches = batchService.getBatchesByProduct(productId);
    return batches.reduce((sum, batch) => sum + batch.quantity, 0);
  }
};
